import React from 'react';
import type { OntologyEnabledBoundedContext } from '@semantical/modules/workspace';
import type { DomainConfig } from '@semantical/modules/workspace';
import { TagColorPicker } from '@semantical/components/toolbox/TagColorPicker';
import { NamespaceTag } from '@semantical/components/toolbox/NamespaceTag';

type Props = {
    boundedContext: OntologyEnabledBoundedContext;
    activeDomain: DomainConfig;
    darkMode: boolean;
    onClose: () => void;
    onNamespaceColorChange?: (namespace: string, color: string) => void;
};

type State = {
    colorPickerState: {
        namespace: string;
        anchorRect: DOMRect;
    } | null;
};

export default class BoundedContextInfoModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            colorPickerState: null
        };
    }

    handleColorSwatchClick = (namespace: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        this.setState({ colorPickerState: { namespace, anchorRect: rect } });
    };

    handleColorSelect = (color: string) => {
        if (this.state.colorPickerState) {
            this.props.onNamespaceColorChange?.(
                this.state.colorPickerState.namespace,
                color
            );
        }
        this.setState({ colorPickerState: null });
    };

    closeColorPicker = () => {
        this.setState({ colorPickerState: null });
    };

    // Helper to find prefix and actual namespace URI for an ontology
    // Handles cases where ontology IRI differs from term namespace (e.g., dcat vs dcat#)
    findPrefixAndNamespace = (ontologyNamespace: string, prefixes: Map<string, string>): { prefix?: string; namespace: string } => {
        // Try exact match first
        let prefix = prefixes.get(ontologyNamespace);
        if (prefix) {
            return { prefix, namespace: ontologyNamespace };
        }

        // Try with # appended
        const withHash = ontologyNamespace.endsWith('#') ? ontologyNamespace : ontologyNamespace + '#';
        prefix = prefixes.get(withHash);
        if (prefix) {
            return { prefix, namespace: withHash };
        }

        // Try with / appended
        const withSlash = ontologyNamespace.endsWith('/') ? ontologyNamespace : ontologyNamespace + '/';
        prefix = prefixes.get(withSlash);
        if (prefix) {
            return { prefix, namespace: withSlash };
        }

        // Try to find a prefix where the namespace starts with the ontology namespace
        for (const [ns, pre] of prefixes.entries()) {
            if (ns.startsWith(ontologyNamespace)) {
                return { prefix: pre, namespace: ns };
            }
        }

        // No prefix found
        return { namespace: ontologyNamespace };
    };

    render() {
        const { boundedContext, activeDomain, onClose } = this.props;

        // Get bounded context information
        const ontologies = boundedContext.getAllOntologies();
        const prefixes = boundedContext.getPrefixes();

        // Collect imports organized by source ontology
        const importsByOntology = ontologies.map(ontology => ({
            ontology,
            imports: ontology.imports
        })).filter(item => item.imports.length > 0);

        // Also collect all unique imports for the count
        const allImports = new Set<string>();
        ontologies.forEach(ontology => {
            ontology.imports.forEach(importIri => allImports.add(importIri));
        });

        // Check which imports are disallowed (by default all are allowed)
        const disallowedImportNs = new Set(activeDomain.disallowedImportNs || []);

        // Track which ontologies are dependencies
        const dependencyNamespaces = new Set(activeDomain.dependencies || []);

        // Helper to classify ontology loading source
        const getOntologyLoadSource = (ontology: any): 'primary' | 'dependency' | 'import' => {
            const ns = ontology.getNamespace();
            if (ns === activeDomain.ns) return 'primary';
            if (dependencyNamespaces.has(ns)) return 'dependency';
            return 'import';
        };

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
                <div className="bg-white dark:bg-surface-dark-elevated text-slate-700 dark:text-white rounded-lg w-[80%] max-w-[900px] max-h-[80vh] shadow-[0_8px_32px_rgba(100,120,140,0.2)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-accent-border flex flex-col">
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-5">
                        <h3 className="mt-0 text-slate-800 dark:text-white mb-4">Bounded Context Information</h3>

                    {/* Bounded Context Section */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-slate-700 dark:text-white/90 mb-3 pb-2 border-b border-slate-200 dark:border-white/10">
                            Bounded Context
                        </h4>
                        <div className="space-y-3">
                            <div className="flex items-baseline gap-2">
                                <span className="text-slate-500 dark:text-white/60 text-xs">Loaded Ontologies:</span>
                                <span className="text-accent-teal dark:text-accent font-semibold text-lg">
                                    {ontologies.length}
                                </span>
                            </div>

                            {ontologies.length > 0 && (
                                <div>
                                    <div className="text-slate-500 dark:text-white/60 text-xs mb-2">Ontology IRIs:</div>
                                    <div className="bg-slate-50 dark:bg-[#202030] rounded-md p-3 border border-slate-200 dark:border-accent/30">
                                        {ontologies.map((ontology, idx) => {
                                            const loadSource = getOntologyLoadSource(ontology);
                                            const badgeIcon = loadSource === 'primary'
                                                ? { svg: <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60"><path d="M7 2L2 6V12H5V9H9V12H12V6L7 2Z" fill="currentColor" stroke="currentColor" strokeWidth="0.3" strokeLinejoin="round"/></svg>, title: 'Primary/Active domain ontology' }
                                                : loadSource === 'dependency'
                                                ? { svg: <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60"><path d="M4 5C4 3.89543 4.89543 3 6 3C7.10457 3 8 3.89543 8 5C8 5.74028 7.5978 6.38663 7 6.73244M10 9C10 7.89543 9.10457 7 8 7C6.89543 7 6 7.89543 6 9C6 10.1046 6.89543 11 8 11C9.10457 11 10 10.1046 10 9Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>, title: 'Dependency (linked ontology)' }
                                                : { svg: <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60"><path d="M7 2V8M7 8L4 6M7 8L10 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><rect x="3" y="9" width="8" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.3" fill="none"/></svg>, title: 'Imported (via owl:imports)' };

                                            const ontologyNs = ontology.getNamespace();
                                            const { prefix, namespace: actualNs } = this.findPrefixAndNamespace(ontologyNs, prefixes);
                                            const color = boundedContext.getNamespaceColor(actualNs);

                                            return (
                                                <div key={idx} className="mb-2 last:mb-0">
                                                    <div className="flex items-start gap-2">
                                                        <div
                                                            className="flex-shrink-0 mt-1"
                                                            title={badgeIcon.title}
                                                        >
                                                            {badgeIcon.svg}
                                                        </div>
                                                        {prefix && (
                                                            <div className="flex-shrink-0 mt-0.5">
                                                                <NamespaceTag
                                                                    prefix={prefix}
                                                                    color={color}
                                                                    onClick={(e) => this.handleColorSwatchClick(actualNs, e)}
                                                                    darkMode={this.props.darkMode}
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-mono text-xs text-slate-700 dark:text-white break-all">
                                                                {ontology.ontologyIRI || ontology.name}
                                                            </div>
                                                            {ontology.versionIRI && (
                                                                <div className="font-mono text-[10px] text-slate-500 dark:text-white/50 mt-0.5">
                                                                    version: {ontology.versionIRI}
                                                                </div>
                                                            )}
                                                            <div className="font-mono text-[10px] text-slate-500 dark:text-white/50">
                                                                prefix: {actualNs}
                                                            </div>
                                                            {ontology.sourceUrl && (
                                                                <div className="font-mono text-[10px] text-slate-400 dark:text-white/40 mt-0.5">
                                                                    source: {ontology.sourceUrl}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-2 flex items-center gap-3 text-[10px] flex-wrap">
                                        <div className="flex items-center gap-1.5">
                                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60">
                                                <path d="M7 2L2 6V12H5V9H9V12H12V6L7 2Z" fill="currentColor" stroke="currentColor" strokeWidth="0.3" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="text-slate-500 dark:text-white/60">Primary (active domain)</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60">
                                                <path d="M4 5C4 3.89543 4.89543 3 6 3C7.10457 3 8 3.89543 8 5C8 5.74028 7.5978 6.38663 7 6.73244M10 9C10 7.89543 9.10457 7 8 7C6.89543 7 6 7.89543 6 9C6 10.1046 6.89543 11 8 11C9.10457 11 10 10.1046 10 9Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                                            </svg>
                                            <span className="text-slate-500 dark:text-white/60">Dependency (linked)</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="text-slate-500 dark:text-white/60">
                                                <path d="M7 2V8M7 8L4 6M7 8L10 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                                <rect x="3" y="9" width="8" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                                            </svg>
                                            <span className="text-slate-500 dark:text-white/60">Import (owl:imports)</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {allImports.size > 0 && (
                                <div>
                                    <div className="text-slate-500 dark:text-white/60 text-xs mb-2">
                                        Imports ({allImports.size} unique):
                                    </div>
                                    <div className="bg-slate-50 dark:bg-[#202030] rounded-md p-3 border border-slate-200 dark:border-accent/30">
                                        {importsByOntology.map((item, idx) => (
                                            <div key={idx} className="mb-3 last:mb-0">
                                                <div className="font-mono text-[10px] text-slate-600 dark:text-white/70 font-semibold mb-1.5">
                                                    {item.ontology.ontologyIRI || item.ontology.name}
                                                </div>
                                                <div className="ml-3 space-y-1">
                                                    {item.imports.map((importIri, importIdx) => {
                                                        const isDisallowed = disallowedImportNs.has(importIri);

                                                        // Check if this import was found/loaded as an ontology
                                                        // An import can reference either the ontology IRI or a prefix namespace
                                                        const isLoaded = ontologies.some(ont => {
                                                            const ontIRI = ont.ontologyIRI || ont.name;
                                                            const ns = ont.getNamespace();

                                                            // Check if importIri matches the ontology IRI (with variations)
                                                            if (ontIRI === importIri ||
                                                                ontIRI === importIri + '#' ||
                                                                ontIRI === importIri + '/' ||
                                                                ontIRI + '#' === importIri ||
                                                                ontIRI + '/' === importIri) {
                                                                return true;
                                                            }

                                                            // Check if importIri matches the namespace (with variations)
                                                            if (ns === importIri ||
                                                                ns === importIri + '#' ||
                                                                ns === importIri + '/' ||
                                                                ns + '#' === importIri ||
                                                                ns + '/' === importIri) {
                                                                return true;
                                                            }

                                                            return false;
                                                        }) || Array.from(prefixes.keys()).some(prefixNs => {
                                                            // Also check if importIri is registered as a prefix namespace
                                                            return prefixNs === importIri ||
                                                                   prefixNs === importIri + '#' ||
                                                                   prefixNs === importIri + '/' ||
                                                                   prefixNs + '#' === importIri ||
                                                                   prefixNs + '/' === importIri;
                                                        });

                                                        return (
                                                            <div key={importIdx} className="flex items-start gap-2">
                                                                <span className="flex-shrink-0 text-slate-400 dark:text-white/40 text-xs mt-0.5">↳</span>
                                                                <span
                                                                    className={`flex-shrink-0 inline-block w-3 h-3 rounded-full mt-0.5 ${
                                                                        isDisallowed
                                                                            ? 'bg-red-400 dark:bg-red-500'
                                                                            : 'bg-emerald-500 dark:bg-emerald-400'
                                                                    }`}
                                                                    title={isDisallowed ? 'Blocked import (in disallowed list)' : 'Allowed import'}
                                                                />
                                                                {isLoaded ? (
                                                                    <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400">
                                                                        <title>Ontology found and loaded</title>
                                                                        <path d="M10 3L4.5 8.5L2 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                ) : (
                                                                    <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0 mt-0.5 text-amber-500 dark:text-amber-400">
                                                                        <title>Ontology not found/loaded</title>
                                                                        <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                                    </svg>
                                                                )}
                                                                <span className="font-mono text-xs text-slate-700 dark:text-white break-all flex-1">
                                                                    {importIri}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 flex items-center gap-4 text-[10px] flex-wrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                                            <span className="text-slate-500 dark:text-white/60">Allowed (default)</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="inline-block w-2 h-2 rounded-full bg-red-400 dark:bg-red-500" />
                                            <span className="text-slate-500 dark:text-white/60">Blocked (disallowed)</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="10" height="10" viewBox="0 0 12 12" className="text-emerald-600 dark:text-emerald-400">
                                                <path d="M10 3L4.5 8.5L2 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="text-slate-500 dark:text-white/60">Loaded</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="10" height="10" viewBox="0 0 12 12" className="text-amber-500 dark:text-amber-400">
                                                <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                            <span className="text-slate-500 dark:text-white/60">Not found</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-baseline gap-2">
                                <span className="text-slate-500 dark:text-white/60 text-xs">Total Prefixes:</span>
                                <span className="text-accent-teal dark:text-accent font-semibold">
                                    {prefixes.size}
                                </span>
                            </div>

                            {prefixes.size > 0 && (
                                <div>
                                    <div className="text-slate-500 dark:text-white/60 text-xs mb-2">Prefix Mappings:</div>
                                    <div className="bg-slate-50 dark:bg-[#202030] rounded-md p-3 border border-slate-200 dark:border-accent/30">
                                        {Array.from(prefixes.entries()).map(([namespace, prefix], idx) => (
                                            <div key={idx} className="font-mono text-xs text-slate-700 dark:text-white mb-1 last:mb-0">
                                                <span className="text-accent-teal dark:text-accent">{prefix}:</span>
                                                <span className="text-slate-500 dark:text-white/60 ml-2 break-all">{namespace}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Domain Section */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-slate-700 dark:text-white/90 mb-3 pb-2 border-b border-slate-200 dark:border-white/10">
                            Active Domain
                        </h4>
                        <div className="space-y-3">
                            <div>
                                <div className="text-slate-500 dark:text-white/60 text-xs mb-1">Label:</div>
                                <div className="text-slate-700 dark:text-white font-medium">{activeDomain.label}</div>
                            </div>

                            <div>
                                <div className="text-slate-500 dark:text-white/60 text-xs mb-1">Domain ID:</div>
                                <div className="font-mono text-xs text-slate-700 dark:text-white">{activeDomain.id}</div>
                            </div>

                            <div>
                                <div className="text-slate-500 dark:text-white/60 text-xs mb-1">Namespace:</div>
                                <div className="font-mono text-xs text-slate-700 dark:text-white break-all">{activeDomain.ns}</div>
                            </div>

                            {activeDomain.dependencies && activeDomain.dependencies.length > 0 && (
                                <div>
                                    <div className="text-slate-500 dark:text-white/60 text-xs mb-2">
                                        Dependencies ({activeDomain.dependencies.length}):
                                    </div>
                                    <div className="bg-slate-50 dark:bg-[#202030] rounded-md p-3 border border-slate-200 dark:border-accent/30">
                                        {activeDomain.dependencies.map((dep, idx) => (
                                            <div key={idx} className="font-mono text-xs text-slate-700 dark:text-white mb-1 last:mb-0 break-all">
                                                {dep}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeDomain.disallowedImportNs && activeDomain.disallowedImportNs.length > 0 && (
                                <div>
                                    <div className="text-slate-500 dark:text-white/60 text-xs mb-2">
                                        Disallowed Import Namespaces ({activeDomain.disallowedImportNs.length}):
                                    </div>
                                    <div className="bg-slate-50 dark:bg-[#202030] rounded-md p-3 border border-slate-200 dark:border-accent/30">
                                        {activeDomain.disallowedImportNs.map((ns, idx) => (
                                            <div key={idx} className="font-mono text-xs text-slate-700 dark:text-white mb-1 last:mb-0 break-all">
                                                {ns}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-baseline gap-2">
                                <span className="text-slate-500 dark:text-white/60 text-xs">Example TTL Files Available:</span>
                                <span className="text-slate-700 dark:text-white font-medium">
                                    {activeDomain.exampleTtl && Object.keys(activeDomain.exampleTtl).length > 0
                                        ? `Yes (${Object.keys(activeDomain.exampleTtl).length})`
                                        : 'No'}
                                </span>
                            </div>
                        </div>
                    </div>
                    </div>

                    {/* Fixed Footer with Close Button */}
                    <div className="flex-shrink-0 border-t border-slate-200 dark:border-white/10 p-4 bg-white dark:bg-surface-dark-elevated rounded-b-lg">
                        <div className="text-right">
                            <button
                                onClick={onClose}
                                className="bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-white border border-slate-200 dark:border-white/20 px-4 py-2 rounded-md cursor-pointer font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>

                {/* Color Picker */}
                {this.state.colorPickerState && (
                    <TagColorPicker
                        anchorRect={this.state.colorPickerState.anchorRect}
                        currentColor={boundedContext.getNamespaceColor(this.state.colorPickerState.namespace)}
                        onColorSelect={this.handleColorSelect}
                        onClose={this.closeColorPicker}
                    />
                )}
            </div>
        );
    }
}
